import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import SuperAdminCommandPalette from './SuperAdminCommandPalette';
import SuperAdminSidebar from './SuperAdminSidebar';
import SuperAdminTopbar from './SuperAdminTopbar';
import { getSuperAdminBreadcrumbs } from './superAdminNavigation';
import './super-admin-shell.css';

const SIDEBAR_STORAGE_KEY = 'crickarena:super-admin:sidebar-collapsed';

const SuperAdminLayout = () => {
  const { logout, user } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => (
    typeof window !== 'undefined' && window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
  ));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchButtonRef = useRef(null);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const handleEscape = (event) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileOpen]);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      }
      return next;
    });
  };

  return (
    <div className={`sa-app${collapsed ? ' is-sidebar-collapsed' : ''}`}>
      <a className="sa-skip-link" href="#super-admin-content">Skip to content</a>
      <SuperAdminSidebar
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        onToggleCollapsed={toggleCollapsed}
      />
      {mobileOpen ? <button className="sa-sidebar-backdrop" type="button" aria-label="Close navigation" onClick={() => setMobileOpen(false)} /> : null}
      <div className="sa-app__workspace">
        <SuperAdminTopbar
          breadcrumbs={getSuperAdminBreadcrumbs(location.pathname)}
          onLogout={logout}
          onOpenMenu={() => setMobileOpen(true)}
          onOpenSearch={() => setSearchOpen(true)}
          searchButtonRef={searchButtonRef}
          user={user}
        />
        <main className="sa-app__canvas" id="super-admin-content" tabIndex="-1">
          <Outlet />
        </main>
      </div>
      <SuperAdminCommandPalette open={searchOpen} onClose={closeSearch} restoreFocusRef={searchButtonRef} />
    </div>
  );
};

export default SuperAdminLayout;
